/**
 * @file Player empty-magazine policy.
 *
 * Every weapon instance rolls its own reserve pool at construction (see
 * `Weapon#constructor` / `rollReserveAmmo`) — a pistol picked up off the floor
 * might already be down to its last few rounds, or bone dry. What happens
 * when the *magazine* hits zero is a single config switch so frenzy pressure
 * can be tuned without a code change.
 */

/** @readonly @enum {string} */
export const EmptyMagazinePolicy = Object.freeze({
  /** R, or an explicit dry trigger, refills from the reserve pool. Default. */
  FREE_RELOAD: 'free_reload',
  /** Empty gun stays equipped; cannot reload. */
  NO_RELOAD: 'no_reload',
  /** Empty gun is dropped; fallback knife is issued. */
  UNEQUIP_EMPTY: 'unequip_empty'
});

/**
 * @param {string | undefined} policy Raw config value.
 * @returns {string} A known policy key.
 */
export function normalizeEmptyMagazinePolicy(policy) {
  if (
    policy === EmptyMagazinePolicy.FREE_RELOAD ||
    policy === EmptyMagazinePolicy.UNEQUIP_EMPTY ||
    policy === EmptyMagazinePolicy.NO_RELOAD
  ) {
    return policy;
  }
  return EmptyMagazinePolicy.FREE_RELOAD;
}

/**
 * Shapes a weapon instance for the player under the active empty-mag policy.
 * Call on every equip path that puts a gun in the player's hands. Enemy AI
 * weapons must not go through this.
 *
 * @param {import('../components/Weapon.js').Weapon} weapon Instance to mutate.
 * @param {string} [policy] One of {@link EmptyMagazinePolicy}.
 * @returns {import('../components/Weapon.js').Weapon} The same weapon.
 */
export function applyPlayerAmmoPolicy(weapon, policy) {
  const mode = normalizeEmptyMagazinePolicy(policy);

  // The reserve pool this instance rolled at construction carries over as-is.
  // Enemies equip with `infiniteReserve: true` (see `enemyAmmoOverrides`); a
  // player picking up their gun draws from the same finite pool as any other.
  weapon.infiniteReserve = false;

  // Knife / true infinite guns are policy-immune.
  if (weapon.infiniteAmmo) return weapon;

  switch (mode) {
    case EmptyMagazinePolicy.FREE_RELOAD:
      weapon.reloadEnabled = true;
      // A dry trigger pull starts the reload when reserve remains; the last
      // fired round itself still leaves the empty state readable for a frame.
      weapon.autoReloadWhenEmpty = true;
      break;
    case EmptyMagazinePolicy.UNEQUIP_EMPTY:
    case EmptyMagazinePolicy.NO_RELOAD:
    default:
      weapon.reloadEnabled = false;
      weapon.autoReloadWhenEmpty = false;
      break;
  }

  return weapon;
}

/**
 * @param {import('../components/Weapon.js').Weapon} weapon Held weapon.
 * @param {string} [policy] Active policy.
 * @returns {boolean} True when the weapon should leave the player's hands.
 */
export function shouldUnequipEmptyWeapon(weapon, policy) {
  if (normalizeEmptyMagazinePolicy(policy) !== EmptyMagazinePolicy.UNEQUIP_EMPTY) {
    return false;
  }
  if (!weapon || weapon.infiniteAmmo) return false;
  return weapon.currentAmmo <= 0 && weapon.droppable !== false;
}

/**
 * Whether a weapon has genuinely nothing left: empty magazine and no reserve
 * to reload from. A weapon with a reserve pool is still worth picking up even
 * mid-reload-window, so this is stricter than "the magazine is empty".
 *
 * @param {import('../components/Weapon.js').Weapon | null | undefined} weapon
 * @returns {boolean}
 */
export function isWeaponDepleted(weapon) {
  if (!weapon || weapon.infiniteAmmo || weapon.infiniteReserve) return false;
  return weapon.currentAmmo <= 0 && weapon.reserveAmmo <= 0;
}

/**
 * What an empty weapon should ask the player to do, if anything.
 *
 * Shared by the desktop center-screen prompt and the touch pad's Reload/Throw
 * glow (see `HUD._showEmptyPrompt` and `TouchControls`), so the two surfaces
 * read the exact same weapon state and can never disagree about which action
 * is actually live.
 *
 * Deliberately blind to `weapon.throwable`: the desktop prompt still shows
 * "WEAPON EMPTY! ... THROW IT" for a weapon that cannot be thrown (only its key
 * badge hides), because there is nothing else useful to tell the player. A
 * caller that needs to know whether *this* is actually pressable — the touch
 * pad deciding whether to glow — checks `weapon.throwable` itself.
 *
 * @param {import('../components/Weapon.js').Weapon | null | undefined} weapon Empty weapon.
 * @returns {'reload' | 'throw' | null} Nothing to do, reload, or throw.
 */
export function emptyWeaponAction(weapon) {
  if (!weapon || weapon.infiniteAmmo) return null;

  const canReload = weapon.reloadEnabled && (weapon.infiniteReserve || weapon.reserveAmmo > 0);
  // Auto-reload already has it covered — nothing for the player to press.
  if (canReload && weapon.autoReloadWhenEmpty) return null;
  return canReload ? 'reload' : 'throw';
}
